import { useEffect, useRef, useState } from 'react';
import maplibregl, { Map as MlMap, Marker } from 'maplibre-gl';
import { booleanPointInPolygon, centroid as turfCentroid, circle as turfCircle, destination, distance, point } from '@turf/turf';
import type { Feature, FeatureCollection, LineString } from 'geojson';
import { COLORS, ORIGIN, METERS_PER_FOOT } from '../../shared/constants';
import type { LngLat, PublicState } from '../../shared/protocol';
import { MAP_STYLE_URL } from '../map/style';
import { hiderPinEl, seekerPinEl } from '../map/pins';
import { boxAround, isOnRoad, nearestRoadPoint, queryRoads } from '../map/roads';

const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };
const GREEN = COLORS.portal;

/** Hider places BOTH boxes (v8 BOLT-006): drag the red ENTRY and the green EXIT
 *  onto roads, each in bounds; the entry must be ≥250 ft from both pins, and the
 *  exit must sit 500–1000 ft from the entry. The exit is the hider's choice — it
 *  is no longer auto-derived along the hider→entry ray (which leaked the hider's
 *  position). A sensible exit is suggested on open; the hider can drag it.
 *  (Curse of the Portal — see portal-curse-spec.md.) */
export function PortalPlacement({
  state,
  onConfirm,
  onCancel,
}: {
  state: PublicState;
  onConfirm: (entry: LngLat, exit: LngLat) => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const roadsRef = useRef<ReturnType<typeof queryRoads>>([]);
  const entryHandle = useRef<Marker | null>(null);
  const exitHandle = useRef<Marker | null>(null);
  const exitTouched = useRef(false); // once the hider drags the exit, stop re-suggesting it
  const [ready, setReady] = useState(false);
  const [valid, setValid] = useState(false);
  const [reason, setReason] = useState('Drag the boxes onto roads');

  const hider = state.selfPin;
  const seeker = state.seekerPin;
  const boundary = state.boundary;
  const sizeFt = state.settings.portalSizeFt;
  const keepFt = state.settings.portalKeepoutFt;
  const minExitFt = state.settings.portalMinExitFt;
  const maxExitFt = state.settings.portalMaxExitFt;

  const distM = (a: LngLat, b: LngLat) => distance(point([a.lng, a.lat]), point([b.lng, b.lat]), { units: 'meters' });
  const inBounds = (p: LngLat) => (boundary ? booleanPointInPolygon([p.lng, p.lat], boundary) : false);

  function setData(id: string, d: Feature | FeatureCollection | null) {
    (mapRef.current?.getSource(id) as maplibregl.GeoJSONSource | undefined)?.setData(d ?? EMPTY);
  }

  /** A reasonable in-band, on-road, in-bounds exit near `entry`. Deliberately
   *  UNCORRELATED with the hider's position (fixed compass bearings, not the
   *  hider→entry ray) so accepting the default doesn't recreate the collinearity
   *  leak — a suggestion only; the hider drags it wherever they like (BOLT-006). */
  function suggestExit(entry: LngLat): LngLat {
    const roads = roadsRef.current;
    const midM = ((minExitFt + maxExitFt) / 2) * METERS_PER_FOOT;
    for (const brng of [90, 270, 0, 180, 45, 135, 225, 315]) {
      const d = destination(point([entry.lng, entry.lat]), midM / 1000, brng, { units: 'kilometers' });
      const at: LngLat = { lat: d.geometry.coordinates[1], lng: d.geometry.coordinates[0] };
      const near = nearestRoadPoint(roads, at);
      const cand = near?.at ?? at;
      const span = distM(entry, cand);
      if (inBounds(cand) && span >= minExitFt * METERS_PER_FOOT && span <= maxExitFt * METERS_PER_FOOT) return cand;
    }
    const d = destination(point([entry.lng, entry.lat]), midM / 1000, 90, { units: 'kilometers' });
    return { lat: d.geometry.coordinates[1], lng: d.geometry.coordinates[0] };
  }

  function recompute() {
    const map = mapRef.current;
    const em = entryHandle.current?.getLngLat();
    const xm = exitHandle.current?.getLngLat();
    if (!map || !boundary || !em || !xm) return;
    const entry: LngLat = { lat: em.lat, lng: em.lng };
    const exit: LngLat = { lat: xm.lat, lng: xm.lng };
    const roads = roadsRef.current;
    const keepM = keepFt * METERS_PER_FOOT;
    const minM = minExitFt * METERS_PER_FOOT;
    const maxM = maxExitFt * METERS_PER_FOOT;

    const eInB = inBounds(entry);
    const eOnRoad = isOnRoad(roads, entry, 45);
    const dH = hider ? distM(entry, hider) : Infinity;
    const dS = seeker ? distM(entry, seeker) : Infinity;
    const entryOk = eInB && eOnRoad && dH >= keepM && dS >= keepM;

    const xInB = inBounds(exit);
    const xOnRoad = isOnRoad(roads, exit, 45);
    const span = distM(entry, exit);
    const exitOk = xInB && xOnRoad && span >= minM && span <= maxM;

    let why = '';
    if (!eInB) why = 'Entry is out of bounds';
    else if (!eOnRoad) why = 'Put the entry on a road';
    else if (dH < keepM) why = `Entry too close to you (${keepFt} ft)`;
    else if (dS < keepM) why = `Entry too close to the seeker (${keepFt} ft)`;
    else if (!xInB) why = 'Exit is out of bounds';
    else if (!xOnRoad) why = 'Put the exit on a road';
    else if (span < minM) why = `Exit too close to the entry (≥ ${minExitFt} ft)`;
    else if (span > maxM) why = `Exit too far from the entry (≤ ${maxExitFt} ft)`;

    const ok = entryOk && exitOk;
    setValid(ok);
    setReason(ok ? 'Tap Arm to set the trap' : why);

    setData('p-entry', boxAround(entry, sizeFt));
    map.setPaintProperty('p-entry-fill', 'fill-color', entryOk ? COLORS.signal : 'rgba(245,241,230,0.25)');
    map.setPaintProperty('p-entry-line', 'line-color', entryOk ? COLORS.signal : COLORS.bone);
    setData('p-exit', boxAround(exit, sizeFt));
    map.setPaintProperty('p-exit-fill', 'fill-color', exitOk ? GREEN : 'rgba(245,241,230,0.2)');
    map.setPaintProperty('p-exit-line', 'line-color', exitOk ? GREEN : COLORS.bone);
    const ray: Feature<LineString> = {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: [[entry.lng, entry.lat], [exit.lng, exit.lat]] },
    };
    setData('p-ray', ray);

    // expose for tests (the hider-placed boxes; no live game state)
    ref.current?.setAttribute('data-portal-entry', `${entry.lat.toFixed(6)},${entry.lng.toFixed(6)}`);
    ref.current?.setAttribute('data-portal-exit', `${exit.lat.toFixed(6)},${exit.lng.toFixed(6)}`);
  }

  function makeHandle(color: string, testid: string, at: LngLat, onDrag: () => void): Marker {
    const el = document.createElement('div');
    el.className = 'portal-handle';
    el.setAttribute('data-testid', testid);
    el.innerHTML = `<svg width="34" height="34" viewBox="0 0 34 34"><circle cx="17" cy="17" r="13" fill="${color}" fill-opacity="0.25"/><circle cx="17" cy="17" r="6" fill="${color}" stroke="${COLORS.slate}" stroke-width="2"/></svg>`;
    const m = new maplibregl.Marker({ element: el, anchor: 'center', draggable: true }).setLngLat([at.lng, at.lat]).addTo(mapRef.current!);
    m.on('drag', onDrag);
    m.on('dragend', onDrag);
    return m;
  }

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const c = hider ?? ORIGIN;
    const map = new maplibregl.Map({
      container: ref.current,
      style: MAP_STYLE_URL,
      center: [c.lng, c.lat],
      zoom: 15.5,
      minZoom: 13,
      maxZoom: 19,
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    map.on('load', () => {
      for (const id of ['boundary', 'p-keepout', 'p-entry', 'p-exit', 'p-ray']) map.addSource(id, { type: 'geojson', data: EMPTY });
      map.addLayer({ id: 'boundary-line', type: 'line', source: 'boundary', paint: { 'line-color': COLORS.bone, 'line-width': 2, 'line-opacity': 0.6, 'line-dasharray': [4, 2.5] } });
      map.addLayer({ id: 'p-keepout-fill', type: 'fill', source: 'p-keepout', paint: { 'fill-color': COLORS.signal, 'fill-opacity': 0.08 } });
      map.addLayer({ id: 'p-exit-fill', type: 'fill', source: 'p-exit', paint: { 'fill-color': GREEN, 'fill-opacity': 0.25 } });
      map.addLayer({ id: 'p-exit-line', type: 'line', source: 'p-exit', paint: { 'line-color': GREEN, 'line-width': 2.5 } });
      map.addLayer({ id: 'p-ray-line', type: 'line', source: 'p-ray', paint: { 'line-color': COLORS.bone, 'line-width': 1.4, 'line-dasharray': [2, 2], 'line-opacity': 0.5 } });
      map.addLayer({ id: 'p-entry-fill', type: 'fill', source: 'p-entry', paint: { 'fill-color': COLORS.signal, 'fill-opacity': 0.22 } });
      map.addLayer({ id: 'p-entry-line', type: 'line', source: 'p-entry', paint: { 'line-color': COLORS.signal, 'line-width': 2.5 } });

      if (boundary) {
        setData('boundary', boundary);
        try {
          map.fitBounds(bbox(boundary), { padding: 50, duration: 0, maxZoom: 17 });
        } catch {
          /* ignore */
        }
      }
      const keepoutFC: FeatureCollection = { type: 'FeatureCollection', features: [] };
      for (const p of [hider, seeker]) if (p) keepoutFC.features.push(turfCircle([p.lng, p.lat], keepFt * METERS_PER_FOOT, { units: 'meters', steps: 48 }));
      setData('p-keepout', keepoutFC);
      if (seeker) new maplibregl.Marker({ element: seekerPinEl(), anchor: 'center' }).setLngLat([seeker.lng, seeker.lat]).addTo(map);
      if (hider) new maplibregl.Marker({ element: hiderPinEl(), anchor: 'center' }).setLngLat([hider.lng, hider.lat]).addTo(map);

      map.once('idle', () => {
        roadsRef.current = queryRoads(map);
        // entry: start on the nearest road to the boundary centroid
        let entryStart: LngLat = boundary ? centerOf(boundary) : c;
        const near = nearestRoadPoint(roadsRef.current, entryStart);
        if (near) entryStart = near.at;
        // exit: a suggested in-band on-road point away from the hider (draggable)
        const exitStart = suggestExit(entryStart);

        entryHandle.current = makeHandle(COLORS.signal, 'portal-handle-entry', entryStart, () => {
          if (!exitTouched.current && entryHandle.current) {
            const e = entryHandle.current.getLngLat();
            const sug = suggestExit({ lat: e.lat, lng: e.lng });
            exitHandle.current?.setLngLat([sug.lng, sug.lat]);
          }
          recompute();
        });
        exitHandle.current = makeHandle(GREEN, 'portal-handle-exit', exitStart, () => {
          exitTouched.current = true;
          recompute();
        });

        recompute();
        setReady(true);
        ref.current?.setAttribute('data-portal-ready', 'true');
      });
    });
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="draw-screen">
      <div ref={ref} className="map-full" data-testid="portal-map" />
      <div className="draw-topbar">
        <span className="draw-topbar__hint">Portal · drag the red entry + green exit onto roads</span>
      </div>
      {ready && (
        <div className="draw-readout" data-testid="portal-readout">
          <span className={`draw-readout__val ${valid ? 'good' : 'bad'}`}>{valid ? 'READY' : 'INVALID'}</span>
          <span className="draw-readout__warn" style={{ color: 'var(--bone-soft)' }}>{reason}</span>
        </div>
      )}
      <div className="draw-actions">
        <button className="btn" onClick={onCancel} data-testid="portal-cancel">
          Cancel
        </button>
        <span />
        <button
          className="btn btn--signal"
          disabled={!valid}
          data-testid="portal-confirm"
          onClick={() => {
            const e = entryHandle.current?.getLngLat();
            const x = exitHandle.current?.getLngLat();
            if (e && x) onConfirm({ lat: e.lat, lng: e.lng }, { lat: x.lat, lng: x.lng });
          }}
        >
          Arm the portal
        </button>
      </div>
    </div>
  );
}

function centerOf(f: Feature): LngLat {
  const c = turfCentroid(f as Feature).geometry.coordinates;
  return { lat: c[1], lng: c[0] };
}
function bbox(f: Feature): [number, number, number, number] {
  let minX = 180,
    minY = 90,
    maxX = -180,
    maxY = -90;
  const walk = (co: any) => {
    if (typeof co[0] === 'number') {
      minX = Math.min(minX, co[0]);
      maxX = Math.max(maxX, co[0]);
      minY = Math.min(minY, co[1]);
      maxY = Math.max(maxY, co[1]);
    } else co.forEach(walk);
  };
  walk((f.geometry as any).coordinates);
  return [minX, minY, maxX, maxY];
}
